import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { ref, onValue, off } from 'firebase/database';
import { database } from '../firebaseConfig';
import L from 'leaflet';
import './TrainMap.css';

// Fix for default markers in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom train icon using the provided SVG
const trainIcon = new L.Icon({
  iconUrl: '/train.svg',
  iconSize: [48, 48], // Made larger as requested
  iconAnchor: [24, 48], // Adjusted anchor point for larger size
  popupAnchor: [0, -48], // Adjusted popup anchor for larger size
});

// Component to handle map reference
const MapController = ({ mapRef, autoFocus, trains }) => {
  const map = useMap();
  
  useEffect(() => {
    mapRef.current = map;
  }, [map, mapRef]);

  useEffect(() => {
    if (autoFocus && Object.keys(trains).length > 0 && map) {
      const trainArray = Object.values(trains);
      if (trainArray.length === 1) {
        const train = trainArray[0];
        map.setView([train.lat, train.lon], 16);
      } else if (trainArray.length > 1) {
        const bounds = L.latLngBounds(
          trainArray.map(train => [train.lat, train.lon])
        );
        map.fitBounds(bounds, { 
          padding: [20, 20],
          maxZoom: 16 
        });
      }
    }
  }, [autoFocus, trains, map]);

  return null;
};

const TrainMap = () => {
  const [trains, setTrains] = useState({});
  const [mapCenter] = useState([39.33, 26.17]); // Default center based on your data
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [autoFocus, setAutoFocus] = useState(true); // Auto-focus enabled by default
  const mapRef = useRef(null);

  useEffect(() => {
    const trainsRef = ref(database, 'trains');
    
    const unsubscribe = onValue(trainsRef, (snapshot) => {
      try {
        setConnectionStatus('connected');
        const data = snapshot.val();
        if (data) {
          setTrains(data);
        } else {
          setTrains({});
        }
      } catch (error) {
        console.error('Error processing train data:', error);
        setConnectionStatus('error');
      }
    }, (error) => {
      console.error('Firebase connection error:', error);
      setConnectionStatus('error');
    });

    return () => {
      off(trainsRef);
      unsubscribe();
    };
  }, []);

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy <= 5) return '#4CAF50'; // Green for high accuracy
    if (accuracy <= 10) return '#FF9800'; // Orange for medium accuracy
    return '#F44336'; // Red for low accuracy
  };

  return (
    <div className="map-container">
      <div className="map-header">
        <h1>Train Location Tracker</h1>
        <div className="status-info">
          <div className={`connection-status ${connectionStatus}`}>
            <span className="status-dot"></span>
            {connectionStatus === 'connected' && 'Connected'}
            {connectionStatus === 'connecting' && 'Connecting...'}
            {connectionStatus === 'error' && 'Connection Error'}
          </div>
          <div className="train-count">
            Active Trains: {Object.keys(trains).length}
          </div>
          <div className="auto-focus-control">
            <label className="switch">
              <input
                type="checkbox"
                checked={autoFocus}
                onChange={(e) => setAutoFocus(e.target.checked)}
              />
              <span className="slider"></span>
            </label>
            <span className="switch-label">Auto Focus</span>
          </div>
        </div>
      </div>
      
      <MapContainer
        center={mapCenter}
        zoom={15}
        style={{ height: 'calc(100vh - 80px)', width: '100%' }}
        zoomControl={true}
      >
        <MapController mapRef={mapRef} autoFocus={autoFocus} trains={trains} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {Object.entries(trains).map(([trainId, trainData]) => (
          <Marker
            key={trainId}
            position={[trainData.lat, trainData.lon]}
            icon={trainIcon}
          >
            <Popup>
              <div className="train-popup">
                <h3>🚂 {trainId}</h3>
                <div className="train-details">
                  <p><strong>Position:</strong> {trainData.lat.toFixed(6)}, {trainData.lon.toFixed(6)}</p>
                  <p><strong>Accuracy:</strong> 
                    <span 
                      className="accuracy-badge"
                      style={{ backgroundColor: getAccuracyColor(trainData.accuracy) }}
                    >
                      ±{trainData.accuracy}m
                    </span>
                  </p>
                  <p><strong>Provider:</strong> {trainData.provider}</p>
                  <p><strong>Last Update:</strong> {formatTimestamp(trainData.timestamp)}</p>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      
      {Object.keys(trains).length === 0 && connectionStatus === 'connected' && (
        <div className="no-trains-message">
          <p>No trains are currently active</p>
        </div>
      )}
    </div>
  );
};

export default TrainMap; 